import { useState, useEffect } from 'react';
import {
  X,
  MessageSquare,
  Trash2,
  MoreVertical,
  Search,
  AlertCircle,
  ChevronLeft,
  Clock,
  User
} from 'lucide-react';

const ChatDrawer = ({ 
  isOpen, 
  onToggle,
  agentId,
  currentChatId,
  onSelectChat,
  onNewChat,
  primaryColor = '#A020F0',
  chatService
}) => {
  const [chats, setChats] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionsMenuOpen, setActionsMenuOpen] = useState(null);
  
  // Load chats when drawer opens or agentId changes
  useEffect(() => {
    if (isOpen && agentId) {
      fetchChats();
    }
  }, [isOpen, agentId]);
  
  const fetchChats = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const result = await chatService.getAllChats({"agent_id": agentId, "channel": "platform"});

      if (result && Array.isArray(result.chats)) {
        setChats(result.chats);
      } else {
        throw new Error('Formato de resposta inválido');
      }
    } catch (err) {
      console.error('Erro ao buscar conversas:', err);
      setError('Não foi possível carregar suas conversas. Por favor, tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDeleteConversation = async (conversationId, e) => {
    e.stopPropagation();
    if (!window.confirm('Tem certeza que deseja excluir esta conversa? Esta ação não pode ser desfeita.')) return;
    
    try {
      await chatService.deleteChat(conversationId, true);
      setChats(prev => prev.filter(conv => conv.id !== conversationId));
      setActionsMenuOpen(null);

      if (currentChatId === conversationId) {
        window.location.reload();
      }
    } catch (err) {
      console.error('Erro ao excluir conversa:', err);
      alert('Falha ao excluir a conversa. Por favor, tente novamente.');
    }
  };
  
  const toggleActionsMenu = (id, e) => {
    e.stopPropagation();
    setActionsMenuOpen(prev => prev === id ? null : id);
  };
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Hoje, ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Ontem, ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays < 7) {
      const days = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
      return days[date.getDay()] + ', ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { day: '2-digit', month: '2-digit', year: '2-digit' });
    }
  };

  // Filter chats based on searchTerm
  const filteredChats = chats.filter(
    conv => (conv.title && conv.title.toLowerCase().includes(searchTerm.toLowerCase())) || 
           (conv.last_message && conv.last_message.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  
  return (
    <>
      {/* Mobile/Desktop Drawer Toggle Button */}
      <button
        className="fixed z-20 right-4 bottom-40 p-4 bg-gradient-to-r from-green-800 to-green-600 hover:from-green-700 hover:to-green-700 rounded-2xl shadow-xl text-white transition-all transform hover:scale-[1.05] active:scale-[0.95] flex items-center gap-2"
        onClick={onToggle}
        aria-label={isOpen ? "Fechar menu de conversas" : "Abrir menu de conversas"}
      >
        {isOpen ? (
          <>
            <ChevronLeft className="w-5 h-5" />
            <span className="hidden md:inline text-sm font-semibold">Fechar</span>
          </>
        ) : (
          <>
            <MessageSquare className="w-5 h-5" />
            <span className="hidden md:inline text-sm font-semibold">Conversas</span>
          </>
        )}
      </button>
      
      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={onToggle}
        />
      )}
      
      {/* Drawer */}
      <div className={`fixed top-0 left-0 h-full bg-white shadow-2xl z-40 transition-all duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } w-full max-w-sm md:max-w-md flex flex-col`}>
        {/* Header */}
        <div className="p-6 bg-gradient-to-r from-green-800 to-green-600 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-xl font-bold">Suas Conversas</h2>
            </div>
            <button 
              onClick={onToggle}
              className="p-2 rounded-xl hover:bg-white/20 transition-all"
              aria-label="Fechar drawer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Search */}
        <div className="p-4 bg-gray-50 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar conversas..."
              className="w-full pl-10 pr-10 py-3 bg-white border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button 
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-lg transition-all"
                onClick={() => setSearchTerm('')}
                aria-label="Limpar busca"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            )}
          </div>
        </div>
        
        {/* List of Chats */}
        <div className="flex-1 overflow-y-auto bg-gray-50">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-40">
              <div className="w-12 h-12 border-4 border-gray-200 border-t-indigo-600 rounded-full animate-spin"></div>
              <p className="mt-3 text-gray-600 font-medium">Carregando conversas...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-40 px-6 text-center">
              <div className="w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center mb-3">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <p className="text-gray-700 font-medium mb-3">{error}</p>
              <button 
                className="px-4 py-2 bg-gradient-to-r from-green-800 to-green-600 text-white rounded-xl hover:from-green-700 hover:to-green-700 font-semibold transition-all transform hover:scale-[1.02] active:scale-[0.98]"
                onClick={fetchChats}
              >
                Tentar novamente
              </button>
            </div>
          ) : filteredChats.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 px-6 text-center">
              {searchTerm ? (
                <>
                  <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center mb-3">
                    <Search className="w-6 h-6 text-gray-400" />
                  </div>
                  <p className="text-gray-700 font-medium">Nenhuma conversa encontrada para:</p>
                  <p className="text-gray-900 font-bold">"{searchTerm}"</p>
                </>
              ) : (
                <>
                  <div className="w-12 h-12 bg-indigo-100 rounded-2xl flex items-center justify-center mb-3">
                    <MessageSquare className="w-6 h-6 text-green-600" />
                  </div>
                  <p className="text-gray-700 font-medium">Nenhuma conversa ainda</p>
                  <p className="text-gray-500 text-sm">Comece uma nova conversa!</p>
                </>
              )}
            </div>
          ) : (
            <div className="p-2">
              {filteredChats.map((chat) => (
                <div
                  key={chat.id}
                  className={`relative mb-2 bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all cursor-pointer group ${
                    currentChatId === chat.id 
                      ? 'ring-2 ring-indigo-500 bg-gradient-to-r from-indigo-50 to-blue-50' 
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={async () => {
                    await onSelectChat(chat.id);
                    if (window.innerWidth < 768) {
                      onToggle();
                    }
                  }}
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-gray-900 truncate pr-2 flex-1">
                        {chat.title || "Conversa sem título"}
                      </h3>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(chat.updated_at || chat.created_at)}
                        </span>
                        <div className="relative">
                          <button
                            className={`p-1.5 rounded-lg ${
                              actionsMenuOpen === chat.id ? 'bg-gray-200' : 'opacity-0 group-hover:opacity-100 hover:bg-gray-100'
                            } transition-all`}
                            onClick={(e) => toggleActionsMenu(chat.id, e)}
                            aria-label="Opções da conversa"
                          >
                            <MoreVertical className="w-4 h-4 text-gray-600" />
                          </button>
                          
                          {actionsMenuOpen === chat.id && (
                            <>
                              <div 
                                className="fixed inset-0 z-40" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActionsMenuOpen(null);
                                }}
                              />
                              <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border z-50 overflow-hidden">
                                <button
                                  className="flex items-center px-4 py-3 text-sm text-red-600 hover:bg-red-50 w-full text-left transition-all"
                                  onClick={(e) => handleDeleteConversation(chat.id, e)}
                                >
                                  <Trash2 className="w-4 h-4 mr-3" />
                                  Excluir conversa
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-600 truncate flex items-center gap-2">
                      <User className="w-3 h-3" />
                      {chat.last_message_preview || "Sem mensagens"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ChatDrawer;